import React, {useContext, useEffect, useState, useRef} from "react";
import axios from "@common/axios";
import CircularLoader from "@common/CircularLoader";
import {BrandContext} from "@context/BrandContext";
import {Alert, Row, Col} from "antd";
import Button from "@common/Button";
import Walkthrough from "./Walkthrough";
import AdminIdTag, {ADMIN_ID_TAG_TYPES} from "@common/AdminIdTag";
import MathJaxHTMLContent from "@common/MathJaxHTMLContent";
import {ContentContext} from "@context/ContentContext";

const Walkthroughs = props => {
    const {conceptId, callback} = props;
    const brandConfig = useContext(BrandContext);
    const [loading, setLoading] = useState(true);
    const [walkthroughs, setWalkthroughs] = useState([]);
    const [activeQuestionId, setActiveQuestionId] = useState(null);
    const loadIntoSummary = useRef(false);
    const {content} = useContext(ContentContext);

    const callbackRef = useRef(callback);
    useEffect(() => { callbackRef.current = callback; });

    useEffect(() => {
        callbackRef.current?.();
        loadWalkthroughs();
    }, []);

    const loadWalkthroughs = () => {
        setLoading(true);
        axios.get("/web/ui/walkthrough/list", {params: {conceptId: conceptId}})
            .then(response => {
                setWalkthroughs(response.data);
            })
            .finally(() => {
                setLoading(false);
            });
    }
    const handleWalkthroughStart = (questionId) => {
        loadIntoSummary.current = false;
        setActiveQuestionId(questionId);
    }
    const handleWalkthroughSummary = (questionId) => {
        loadIntoSummary.current = true;
        setActiveQuestionId(questionId);
    }

    return (
        <>
            <h2 className="margin-b--l">{brandConfig.walkthroughsName}</h2>
            {loading && (
                <Row>
                    <CircularLoader displayInElement={true} />
                </Row>
            )}
            {!loading && walkthroughs.length == 0 && (
                <Alert role="alert" type="warning" title={'There are currently no ' + brandConfig.walkthroughName + 's for this ' + brandConfig.conceptName}/>
            )}
            {!loading && !activeQuestionId && walkthroughs.length > 0 && walkthroughs.map(walkthrough => (
                <div key={walkthrough.questionId}>
                    <Row className="mediumGap" wrap={false}>
                        {brandConfig.showWalkthroughIcon && (
                            <Col flex="64px">
                                <svg
                                    width="64"
                                    height="64"
                                    viewBox="0 0 128 128"
                                    aria-hidden="true"
                                >
                                    <use href="#tryit-icon" />
                                </svg>
                            </Col>
                        )}
                        <Col flex="auto">
                            <AdminIdTag
                                id={walkthrough.questionId}
                                type={ADMIN_ID_TAG_TYPES.QUESTION}
                            />
                            <h3>
                                {brandConfig.walkthroughName} {walkthrough.walkthroughIndex}
                            </h3>
                            <MathJaxHTMLContent
                                className={'margin-t--m ' + content?.currentSyllabus?.syllabusContentClass}
                                html={walkthrough.description}
                                showLoader={false}
                            />
                        </Col>
                    </Row>
                    <Row justify="end" className="margin-t--m mediumGap">
                        <Button
                            onClick={() => handleWalkthroughStart(walkthrough.questionId)}
                            aria-label={`Start ${brandConfig.walkthroughName} ${walkthrough.walkthroughIndex}`}
                        >
                            Start
                        </Button>
                        <Button
                            onClick={() => handleWalkthroughSummary(walkthrough.questionId)}
                            type={"outline"}
                            aria-label={`Summary of ${brandConfig.walkthroughName} ${walkthrough.walkthroughIndex}`}
                        >
                            Summary
                        </Button>
                    </Row>
                </div>
            ))}
            {!loading && activeQuestionId && (
                <div className="margin-t--l">
                    <Walkthrough
                        questionId={activeQuestionId}
                        conceptId={conceptId}
                        showBackBtn={true}
                        showRestartBtn={true}
                        loadIntoSummary={loadIntoSummary.current}
                        onBack={() => setActiveQuestionId(null)}
                        showWalkthroughCompleteBtn={true}
                        walkthroughCompleteBtnLabel={walkthroughs[walkthroughs.length - 1].questionId === activeQuestionId ? "Walkthrough list" : "Next walkthrough"}
                        onWalkthroughComplete={() => {
                            const questionIndex = walkthroughs.findIndex(walkthrough => walkthrough.questionId === activeQuestionId);
                            const nextQuestionId = walkthroughs[questionIndex + 1]?.questionId;
                            setActiveQuestionId(nextQuestionId);
                        }}
                    />
                </div>
            )}
        </>
    );
};

export default Walkthroughs;